import * as React from "react";
import _ from "lodash";
import moment from "moment";
import Globalize from "globalize";
import { format } from "lib/globalize-util";
import app from "lib/app";
import { FormatMessage } from "react-globalize";
import { makeStyles, Theme } from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import Popper from "@material-ui/core/Popper";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import theme from "styles/theme";
import { Form } from "formsy-react";
import TextField from "components/form/text-field";
import { AddHintBadge } from "components/hint-badge";
import Button from "components/form/button";
import { useCallback, useEffect, useState } from "react";
import type { IScope } from "reducers/scope";
import Toggle from "material-ui/Toggle";
import { isPhablet } from "constants/media";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { validateDraft } from "actions/working-actions";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContentText from "@material-ui/core/DialogContentText";
import { InlineLoading } from "components/content-loading";
import { APP_MENU_STATE_PINNED } from "constants/menu";
import { DATATEST } from "constants/integration";
import { saveDraftOrder } from "actions/working-order/draft";
import { IWorkingOrder } from "types/cart";
import me from "models/me";

interface IOwnProps {
    onClose: () => void;
    anchorEl: HTMLElement;
    order: {
        draft_name: string;
        draft_shared_buyer: Array<{ name: string; ref: string }>;
        cc_list: string[];
        retailer: {
            buyer?: string;
            buyer_name?: string;
            ref?: string;
        };
        assigned_rep?: {
            rep_name: string;
        };
        draft: IWorkingOrder;
    };

    open?: boolean;
}

interface IReduxProps {
    viewport: string;
    scope: IScope;
    isValid: boolean;
    error: { summary: string; messages: Array<{ msg: string; id: string }> };
    isValidating: boolean;
    isDraftSaving: boolean;
    menuState: string;
}

interface IDispatchProps {
    onValidate: (draft) => void;
    onSaveDraft: (draft) => void;
}

const useStyles = makeStyles<Theme, { shared: boolean; mobile: boolean; pinned: boolean }>({
    root: ({ mobile, pinned }) => ({
        width: mobile ? "100%" : `calc(100% - ${pinned ? theme.nav.iconAndTextWidth : theme.nav.iconWidth}px)`,
        height: "calc(100% - 60px)",
        marginLeft: mobile ? 0 : `${pinned ? theme.nav.iconAndTextWidth : theme.nav.iconWidth}px`,
        marginTop: 61,
        zIndex: 1000,
        transform: "none !important",
        overflow: "auto",
    }),
    paper: {
        padding: 30,
        height: "100%",
        minHeight: 745,
        minWidth: 600,
    },
    grid: {
        height: "100%",
    },
    header: {
        display: "flex",
        alignItems: "center",
    },
    icon: {
        width: 30,
        height: 30,
        color: theme.palette.primary1Color,
        marginRight: 10,
    },
    headerTitle: {
        fontSize: 24,
        margin: 0,
        display: "inline-block",
        color: theme.palette.darkestGrey,
    },
    date: {
        fontSize: 14,
        fontStyle: "italic",
        color: theme.palette.grey,
    },
    margin: {
        margin: "20px 0",
    },
    input: {
        maxWidth: 500,
    },
    badge: ({ shared }) => ({
        fontSize: 14,
        backgroundColor: shared ? "#d8edf8" : theme.palette.lightGrey,
        color: shared ? "#70a6d6" : "inherit",
        borderRadius: 5,
        display: "inline-block",
        padding: "5px 15px",
        fontStyle: "italic",
        fontWeight: 600,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxWidth: "100%",
    }),
    settingsLabel: {
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.darkestGrey,
        margin: "15px 0",
    },
    link: {
        color: theme.palette.primary1Color,
        cursor: "pointer",
    },
    toggle: {
        minWidth: 300,
        maxWidth: 400,
    },
    ccToggle: {
        top: 13,
    },
    hint: {
        top: 10,
    },
    message: {
        fontSize: 14,
        fontStyle: "italic",
    },
    button: {
        margin: "0 15px !important",
    },
    multiline: {
        marginTop: "20px",
        maxWidth: 900,
        "& textarea": {
            marginTop: "0 !important",
        },
    },
    dialogTitle: {
        fontSize: 20,
    },
    dialogContent: {
        fontSize: 14,
        fontFamily: "inherit",
    },
});

const WorkingOrderDraftModal: React.FC<IOwnProps & IReduxProps & IDispatchProps> = (props) => {
    const {
        onClose,
        anchorEl,
        viewport,
        scope,
        onValidate,
        isValid,
        isValidating,
        error,
        open = true,
        menuState,
        onSaveDraft,
        isDraftSaving,
        order,
    } = props;
    const shared = _.get(order, "draft_shared_buyer.name");
    const repActive = !!_.get(order, "retailer.ref") && !!_.get(order, "assigned_rep.rep_name");

    const defaultName = `${format("Untitled Draft")} - ${moment().locale(Globalize.cldr.locale).format("MMM D, YYYY")}`;

    const classes = useStyles({
        shared: !!shared,
        mobile: isPhablet(viewport),
        pinned: menuState === APP_MENU_STATE_PINNED,
    });

    const [title, setTitle] = useState(_.get(order, "draft_name", ""));
    const [message, setMessage] = useState<string | null>(null);
    const [ccList, setCC] = useState(_.get(order, "cc_list", ""));
    const [emails, setEmails] = useState({
        buyer: scope.accountType === "brand" && !!shared,
        rep: scope.accountType === "brand" && repActive,
        creator: scope.accountType === "brand",
    });
    const [validation, showValidation] = useState(false);

    useEffect(() => {
        const ccListRaw = _.get(order, "cc_list", "");
        const initialCCList = _.isArray(ccListRaw) && !_.isEmpty(ccListRaw) ? ccListRaw : "";
        setCC(initialCCList);
        return onClose;
    }, [_.get(order, "cc_list")]); // eslint-disable-line react-hooks/exhaustive-deps

    const closeValidation = useCallback(() => showValidation(false), []);
    const handleTitleChange = useCallback((e: Event, value: string) => setTitle(value), []);
    const handleMessageChange = useCallback((e: Event, value: string) => setMessage(value), []);
    const handleCCChange = useCallback((e: Event, value: string) => setCC(value), []);
    const handleToggle = (name: "buyer" | "rep" | "creator") => (e, value: boolean) =>
        setEmails({ ...emails, [name]: value });

    const goToDetails = useCallback(() => {
        app.legacy.sendMessage({ action: "wo-tab-change", tab: "details" });
        onClose();
    }, [onClose]);

    const save = useCallback(() => {
        onSaveDraft({
            draftWrapper: {
                subject: Globalize.formatMessage("|name| at |brand| has sent you a draft order!", {
                    name: me.name,
                    brand: scope.brand.name,
                }),
                message: message == null ? null : (message || "").replace(/(\r\n|\n|\r)/gm, "<br />"),
                send_emails: {
                    ...emails,
                    cc: _(ccList)
                        .split(/[,; ]+/)
                        .map(_.trim)
                        .value(),
                },
                draft: {
                    ...order,
                    draft_name: title || defaultName,
                },
            },
        });
    }, [onSaveDraft, scope.brand.name, message, emails, ccList, order, title, defaultName]);

    const validate = useCallback(() => {
        showValidation(true);
        onValidate(order);
    }, [order, onValidate]);

    const fix = useCallback(() => {
        app.legacy.sendMessage({ action: "load-items", messages: _.get(error, "messages") });
        onClose();
    }, [error, onClose]);

    const date = _.get(order, "draft_modified_on");
    const sendEmail = emails.buyer || emails.creator;

    const validationRequired = !_.isEmpty(_.get(props, "scope.brand.settings.order_quantity_rules"));

    return (
        <Popper className={classes.root} open={open} anchorEl={anchorEl} placement="bottom-start">
            <Paper className={classes.paper}>
                <Grid
                    className={classes.grid}
                    container={true}
                    direction="column"
                    justify="space-between"
                    wrap="nowrap"
                    spacing={2}
                >
                    <Grid className={classes.header} id="header" item={true}>
                        <SaveIcon className={classes.icon} />
                        <p className={classes.headerTitle}>{format("Save as Draft")}</p>
                    </Grid>
                    <Grid id="content" item={true} xs={true}>
                        <Form>
                            <Grid id="draft-name" container={true} spacing={1}>
                                <Grid className={classes.input} item={true} xs={true}>
                                    {/* tslint:disable-next-line: max-line-length */}
                                    <AddHintBadge
                                        hint={
                                            "Your draft title will be displayed on the orders tab. If you choose not to name your draft order, the draft title will default to ‘Untitled draft - date’"
                                        }
                                    >
                                        <TextField
                                            dataTest={DATATEST.WORKING_ORDER.DRAFT.DRAFT_ORDER_NAME}
                                            name="title"
                                            placeholder={defaultName}
                                            value={title}
                                            large={true}
                                            fullWidth={true}
                                            onChange={handleTitleChange}
                                        />
                                    </AddHintBadge>
                                </Grid>
                                <Grid item={true} xs={true} container={true} alignItems="center">
                                    {date && (
                                        <div className={classes.date}>
                                            {format("Last saved: |date|", {
                                                date: moment(date)
                                                    .locale(Globalize.cldr.locale)
                                                    .format("MMM D, YYYY @ h:mm A"),
                                            })}
                                        </div>
                                    )}
                                </Grid>
                            </Grid>

                            {scope.accountType !== "buyer" && (
                                <React.Fragment>
                                    <Divider className={classes.margin} variant="fullWidth" />

                                    <div className={classes.settingsLabel}>{format("Notification Settings")}</div>

                                    <div className={classes.badge}>
                                        {shared
                                            ? format("Shared with Buyer: |name|", { name: shared })
                                            : format("Note: This draft has not been shared with the Buyer yet")}
                                    </div>

                                    <Grid className={classes.margin} id="toggles" container={true}>
                                        <Grid className={classes.toggle} item={true} xs={true}>
                                            {_.get(order, "retailer.buyer") ? (
                                                <Toggle
                                                    toggled={emails.buyer}
                                                    onToggle={handleToggle("buyer")}
                                                    label={format("Email to Buyer: |name|", {
                                                        name: order.retailer.buyer_name,
                                                    })}
                                                    labelPosition="right"
                                                />
                                            ) : (
                                                <FormatMessage
                                                    className={classes.message}
                                                    path="No Rep selected. Select a Rep first on the [link]order details[link] tab."
                                                    elements={{
                                                        link: <span className={classes.link} onClick={goToDetails} />,
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                        <Grid className={classes.toggle} item={true} xs={true}>
                                            {scope.accountType === "nuorder" &&
                                                (_.get(order, "retailer.ref") &&
                                                _.get(order, "assigned_rep.rep_name") ? (
                                                    <Toggle
                                                        toggled={emails.rep}
                                                        onToggle={handleToggle("rep")}
                                                        label={format("Email to Rep: |name|", {
                                                            name: order.assigned_rep.rep_name,
                                                        })}
                                                        labelPosition="right"
                                                    />
                                                ) : (
                                                    <FormatMessage
                                                        className={classes.message}
                                                        path="No Buyer selected. Select a Buyer first on the [link]order details[link] tab."
                                                        elements={{
                                                            link: (
                                                                <span className={classes.link} onClick={goToDetails} />
                                                            ),
                                                        }}
                                                    />
                                                ))}
                                        </Grid>
                                    </Grid>

                                    <Grid container={true} id="cc-emails" spacing={1}>
                                        <Grid item={true} className={classes.input} xs={8}>
                                            <AddHintBadge
                                                classes={{ button: classes.hint }}
                                                hint={
                                                    "Adding an e-mail address to the CC field only sends a copy of the e-mail"
                                                }
                                            >
                                                <TextField
                                                    label={format("Additional recipient(s)")}
                                                    placeholder={format("Enter CC emails (optional)")}
                                                    name="cc"
                                                    value={ccList}
                                                    large={true}
                                                    fullWidth={true}
                                                    onChange={handleCCChange}
                                                    disabled={!sendEmail}
                                                />
                                            </AddHintBadge>
                                        </Grid>
                                        <Grid item={true} xs={4} container={true} alignItems="center">
                                            <Toggle
                                                className={classes.ccToggle}
                                                labelPosition="right"
                                                toggled={emails.creator}
                                                onToggle={handleToggle("creator")}
                                                label={format("CC myself")}
                                            />
                                        </Grid>
                                    </Grid>
                                    <AddHintBadge
                                        classes={{ root: classes.multiline }}
                                        hint={"This message will be displayed in the e-mail"}
                                    >
                                        <TextField
                                            name="message"
                                            label={format("Message")}
                                            value={
                                                message ??
                                                format(
                                                    "I have created a(n) Draft Order for you. A summary of the order is below."
                                                )
                                            }
                                            onChange={handleMessageChange}
                                            multiLine={true}
                                            fullWidth={true}
                                            rows={6}
                                            rowsMax={6}
                                            disabled={!sendEmail}
                                        />
                                    </AddHintBadge>
                                </React.Fragment>
                            )}
                        </Form>
                    </Grid>
                    <Grid id="actions" item={true} container={true} justify="space-between">
                        <Grid item={true}>
                            {validationRequired && (
                                <AddHintBadge hint={"See if your order does not meet order minimum or maximum rules"}>
                                    <Button label={format("Validate")} onClick={validate} />
                                </AddHintBadge>
                            )}
                        </Grid>
                        <Grid item={true}>
                            <Button className={classes.button} label={format("Cancel")} onClick={onClose} />
                            <Button
                                data-test={DATATEST.WORKING_ORDER.DRAFT.SAVE_DRAFT_ORDER}
                                label={sendEmail ? format("Save & Send") : format("Save")}
                                onClick={save}
                                primary={true}
                                disabled={isDraftSaving}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
            <Dialog open={validation} onClose={closeValidation} maxWidth="xs" fullWidth={true}>
                <DialogTitle className={classes.dialogTitle} disableTypography={true}>
                    {format("Order Validation")}
                </DialogTitle>
                <DialogContent>
                    {isValidating ? (
                        <InlineLoading margin="5px" />
                    ) : (
                        <DialogContentText className={classes.dialogContent}>
                            {isValid ? format("The order is valid!") : _.get(error, "summary")}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    {!isValidating && isValid === false && (
                        <Button className={classes.button} label={format("Fix")} onClick={fix} />
                    )}
                    <Button label={format("Close")} onClick={closeValidation} primary={true} />
                </DialogActions>
            </Dialog>
        </Popper>
    );
};

const mapStateToProps = (store: IStore): IReduxProps => {
    return {
        isValid: store.working.drafts.isValid,
        error: store.working.drafts.validationError,
        isDraftSaving: store.working.drafts.isSaving,
        isValidating: store.working.drafts.isValidating,
        viewport: store.app.viewport,
        scope: store.scope,
        menuState: store.app.menuState,
    };
};

const mapDispatchToProps = (dispatch): IDispatchProps => ({
    onValidate: (draft) => dispatch(validateDraft(draft)),
    onSaveDraft: (draft) => dispatch(saveDraftOrder(draft)),
});

export default connect<IReduxProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps
)(WorkingOrderDraftModal);
